import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { Dashboard as DashboardData } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { Empty, Notice } from '../components/ui'

const STYLE_LABEL: Record<string, string> = { NUMBER: '数字', ALPHA: '字母', MIX: '混合', REVIEW: '其他' }
// 每种类型一个固定颜色：看板一天要看几十眼，颜色稳定下来后"蓝色那行是数字"就不用再想了。
const STYLE_COLOR: Record<string, string> = {
  NUMBER: 'var(--s-number)',
  ALPHA: 'var(--s-alpha)',
  MIX: 'var(--s-mix)',
  REVIEW: 'var(--s-review)',
}
const STYLE_ORDER = ['NUMBER', 'ALPHA', 'MIX', 'REVIEW']

export function Dashboard() {
  const { data, error, loading, reload } = useAsync(() => api.get<DashboardData>('/api/dashboard'), [])
  const [open, setOpen] = useState<Set<string>>(new Set())

  function toggle(suffix: string) {
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(suffix)) next.delete(suffix)
      else next.add(suffix)
      return next
    })
  }

  return (
    <section>
      <header className="page-head compact">
        <h1>后缀看板</h1>
        <div className="row">
          {data && (
            <div className="dash-stats">
              <span>
                <b>{data.totals.inStock}</b>
                <em>在库</em>
              </span>
              <span>
                <b>{data.totals.suffixes}</b>
                <em>种后缀</em>
              </span>
            </div>
          )}
          <button type="button" onClick={reload} disabled={loading}>
            {loading ? '刷新中…' : '刷新'}
          </button>
        </div>
      </header>

      <Notice error={error} />
      {!error && (!data || (data.cards ?? []).length === 0) && <Empty text="还没有在库域名，先去「入库」粘贴一批。" />}

      <div className="cards">
        {(data?.cards ?? []).map((c) => {
          const isOpen = open.has(c.suffix)
          // 展开后按"数量从多到少"排类型，和卡片正面的顺序一致，眼睛不用重新找。
          const styles = Object.entries(c.styles ?? {})
            .sort((a, b) => b[1] - a[1] || STYLE_ORDER.indexOf(a[0]) - STYLE_ORDER.indexOf(b[0]))
          return (
            <article key={c.suffix} className={'card suffix' + (isOpen ? ' open' : '')}>
              <header>
                <h2>
                  <Link to={`/inventory?suffix=${encodeURIComponent(c.suffix)}&status=IN_STOCK`}>{c.suffix}</Link>
                </h2>
                <span className="count">
                  {c.total}
                  <span className="sr-only"> 个在库</span>
                </span>
              </header>
              {c.total > 0 && styles.length > 0 && (
                <div className="mix" aria-hidden="true">
                  {styles.map(([k, v]) => (
                    <i
                      key={k}
                      title={`${STYLE_LABEL[k] ?? k} ${v}`}
                      style={
                        {
                          flexGrow: v,
                          '--c': STYLE_COLOR[k] ?? 'var(--accent)',
                        } as React.CSSProperties
                      }
                    />
                  ))}
                </div>
              )}
              <ul className="mix-legend">
                {styles.map(([k, v]) => (
                  <li key={k}>
                    <i style={{ '--c': STYLE_COLOR[k] ?? 'var(--accent)' } as React.CSSProperties} />
                    <span>{STYLE_LABEL[k] ?? k}</span>
                    <b>{v}</b>
                  </li>
                ))}
              </ul>
              <div className="card-foot">
                <button type="button" className="link dash-act" onClick={() => toggle(c.suffix)} aria-expanded={isOpen}>
                  {isOpen ? '收起详情' : '查看详情'}
                </button>
                <Link className="link dash-act" to={`/inventory?suffix=${encodeURIComponent(c.suffix)}&status=IN_STOCK`}>
                  打开总库
                </Link>
              </div>
              {isOpen && (
                <div className="detail">
                  {styles.map(([k, v]) => (
                    <section key={k} className="detail-block">
                      <h3>
                        <i style={{ '--c': STYLE_COLOR[k] ?? 'var(--accent)' } as React.CSSProperties} />
                        {STYLE_LABEL[k] ?? k}
                        <span className="muted">{v}</span>
                      </h3>
                      <ul>
                        {(c.byStyle?.[k] ?? []).map((l) => (
                          <li key={l.charCount}>
                            <Link
                              to={`/inventory?suffix=${encodeURIComponent(c.suffix)}&status=IN_STOCK&style=${k}&charCount=${l.charCount}`}
                            >
                              {l.charCount} 字符
                            </Link>
                            <b>{l.count}</b>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              )}
            </article>
          )
        })}
      </div>
    </section>
  )
}
